import { ActionTree } from 'vuex'
import { Things } from './types'
import { RootState } from '../types'
import { ThingsHelpers } from '@/helpers/mainExports.js'
import { i18n } from '@leapsight/portal'
import store from '@/store'

export const actions: ActionTree<Things, RootState> = {
  getThingsList({ dispatch }, { page, things, total } = { page: 1, things: [], total: 0 }) {
    return new Promise((resolve, reject) => {
      ThingsHelpers.getThingsList({ page })
        .then(response => {
          things = things.concat(response.args[0])

          if (total === 0) {
            total = response.kwargs.total || 0
          }

          if (things.length >= total || response.args[0].length === 0) {
            dispatch('setResponseError', false)
            dispatch('setThings', things)
            resolve(things)
          } else {
            page++
            dispatch('getThingsList', { page, things, total })
              .then(response => {
                resolve(response)
              })
              .catch(error => {
                reject(error)
              })
          }
        })
        .catch(error => {
          const errorText = ThingsHelpers.getErrorMessageByCode(error.error)
          const snackbar = {
            color: 'error',
            text: errorText
          }
          store.dispatch('ui/setSnackbar', snackbar)
          reject(error)
        })
    })
  },
  fetchThing({ dispatch }, thingId) {
    return new Promise<void>(resolve => {
      ThingsHelpers.getThing(thingId)
        .then(response => {
          dispatch('setResponseError', false)
          dispatch('setCurrentThing', response.args[0]).then(() => {
            resolve()
          })
        })
        .catch(() => {
          dispatch('setResponseError', true)
          const response = i18n.t(
            'fleet_management.entities.entities_tab_all.errors.no_info'
          )
          dispatch('setResponseMessage', response)
          resolve()
        })
    })
  },
  setThings({ commit }, payload) {
    //commit('cleanThingsState')

    if (payload.length > 0) {
      payload.forEach(thing => {
        const id = ThingsHelpers.getThingId(thing)
        const uriId = thing.id
        const name = thing.info.label
        const aliasesQuantity = thing.aliases_qty
        const isAlias = thing.is_alias
        const type = ThingsHelpers.getThingType(thing)
        //Actualmente el thing "flex" no tiene subtype, al momento se asigna el type como subtype
        const subType = thing.info.subtype ? thing.info.subtype : type
        const tags = thing.info.tags ?? []
        const domain = ThingsHelpers.getThingDomain(thing)
        const lastReport = !thing.metadata
          ? ''
          : thing.metadata.reported_state.hasOwnProperty('location')
          ? thing.metadata.reported_state.location.timestamp
          : ''
        const agents = ThingsHelpers.getThingAgents(thing)
        const serviceName = ThingsHelpers.getThingServiceSolution(thing)
        const serviceStatus = ThingsHelpers.getThingServiceState(thing)
        const created = ThingsHelpers.getThingCreated(thing)
        const serviceList = ThingsHelpers.getThingServiceList(thing)

        let dataThing = {}

        switch (type) {
          case 'vehicle':
            const make = ThingsHelpers.getThingMake(thing)
            const model = ThingsHelpers.getThingModel(thing)
            const year = ThingsHelpers.getThingYear(thing)
            const color = ThingsHelpers.getThingColor(thing)
            const chasisNumber = ThingsHelpers.getThingChasisNumber(thing)
            const engineNumber = ThingsHelpers.getThingEngineNumber(thing)
            const mileage_reading = ThingsHelpers.getThingMileageReading(thing)
            const usage_reading = ThingsHelpers.getThingUsageReading(thing)

            dataThing = {
              id,
              uriId,
              name,
              aliasesQuantity,
              isAlias,
              type,
              subType,
              tags,
              domain,
              lastReport,
              agents,
              make,
              model,
              year,
              color,
              chasisNumber,
              engineNumber,
              serviceName,
              serviceStatus,
              created,
              serviceList,
              mileage_reading,
              usage_reading
            }
            break
          case 'flex':
            dataThing = {
              id,
              uriId,
              name,
              aliasesQuantity,
              isAlias,
              type,
              subType,
              tags,
              domain,
              lastReport,
              agents,
              serviceName,
              serviceStatus,
              created,
              serviceList
            }
            break
          default:
            break
        }

        commit('setThings', dataThing)
      })
    }
    commit('resetFilterThingsState')
  },
  async setCurrentThing({ commit }, thing) {
    return new Promise<void>((resolve, reject) => {
      const id = ThingsHelpers.getThingId(thing)
      const uriId = thing.id
      const name = thing.name ? thing.name : thing.info.label
      const aliasesQuantity = thing.aliases_qty ?? thing.aliasesQuantity
      const isAlias = thing.is_alias ?? thing.isAlias
      const photo = thing.info?.photo
      const type = thing.type
        ? thing.type.slice(10)
        : ThingsHelpers.getThingType(thing)
      //Actualmente el thing "flex" no tiene subtype, al momento se asigna el type como subtype
      let subType
      if (thing.subType) {
        subType = thing.subType
      } else {
        subType = thing.info.subtype ? thing.info.subtype : type
      }
      const tags = thing.tags ?? thing.info.tags ?? []
      const domain = thing.domain
        ? thing.domain
        : ThingsHelpers.getThingDomain(thing)
      let lastReport
      if (thing.lastReport) {
        lastReport = thing.lastReport
      } else {
        lastReport = !thing.metadata
          ? ''
          : thing.metadata.reported_state.hasOwnProperty('location')
          ? thing.metadata.reported_state.location.timestamp
          : ''
      }
      const agents = thing.agents
        ? thing.agents
        : ThingsHelpers.getThingAgents(thing)
      const serviceName = thing.serviceName
        ? thing.serviceName
        : ThingsHelpers.getThingServiceSolution(thing)
      const serviceStatus = thing.serviceStatus
        ? thing.serviceStatus
        : ThingsHelpers.getThingServiceState(thing)
      const created = thing.created
        ? thing.created
        : ThingsHelpers.getThingCreated(thing)
      const serviceList = thing.serviceList
        ? thing.serviceList
        : ThingsHelpers.getThingServiceList(thing)
      const additionalInfo = thing.info ? thing.info.user_info : {}

      let dataThing = {}

      switch (type) {
        case 'vehicle':
          const make = thing.make
            ? thing.make
            : ThingsHelpers.getThingMake(thing)
          const model = thing.model
            ? thing.model
            : ThingsHelpers.getThingModel(thing)
          const year = thing.year
            ? thing.year
            : ThingsHelpers.getThingYear(thing)
          const color = thing.color
            ? thing.color
            : ThingsHelpers.getThingColor(thing)
          const chasisNumber = thing.chasisNumber
            ? thing.chasisNumber
            : ThingsHelpers.getThingChasisNumber(thing)
          const engineNumber = thing.engineNumber
            ? thing.engineNumber
            : ThingsHelpers.getThingEngineNumber(thing)
          const mileage_reading = thing.mileage_reading
            ? thing.mileage_reading
            : ThingsHelpers.getThingMileageReading(thing)
          const usage_reading = thing.usage_reading
            ? thing.usage_reading
            : ThingsHelpers.getThingUsageReading(thing)

          dataThing = {
            id,
            uriId,
            name,
            aliasesQuantity,
            isAlias,
            photo,
            type,
            subType,
            tags,
            domain,
            lastReport,
            agents,
            make,
            model,
            year,
            color,
            chasisNumber,
            engineNumber,
            serviceName,
            serviceStatus,
            created,
            serviceList,
            mileage_reading,
            usage_reading,
            additionalInfo
          }
          break
        case 'flex':
          dataThing = {
            id,
            uriId,
            name,
            aliasesQuantity,
            isAlias,
            photo,
            type,
            subType,
            tags,
            domain,
            lastReport,
            agents,
            serviceName,
            serviceStatus,
            created,
            serviceList,
            additionalInfo
          }
          break
        default:
          break
      }

      commit('setCurrentThing', dataThing)
      resolve()
      // setTimeout(() => {
      //   commit('someMutation')

      // }, 1000)
    })
  },
  cleanThingsState({ commit }) {
    commit('cleanThingsState')
  },
  setResponseError({ commit }, payload) {
    commit('setResponseError', payload)
  },
  setResponseMessage({ commit }, message) {
    commit('setResponseMessage', message)
  },
  updateThingName({ commit }, payload) {
    commit('updateThingName', payload)
  },
  filterThingsByString({ state, dispatch, commit }, { string }) {
    if (string.length > 1) {
      const filterTings = state.things.filter(
        thing =>
          thing.name.toLowerCase().match(string) ||
          thing.domain.toLowerCase().match(string) ||
          thing.serviceStatus.toLowerCase().match(string)
      )
      commit('setFilterThingsByString', { filterTings })
    } else commit('resetFilterThingsState')
  }
}
